import React from 'react';
import { useTranslation } from 'react-i18next';
import { benchmarkDay, FarmBenchmarkMetric, FarmBenchmarkWeek } from '../../models/farm-benchmark';
import { AnchorRect, useAnchorRect } from '../../hooks/useAnchorRect';
import { HoverCard } from '../../elements/ui/hover-card';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import { farmBenchmarkColors as c, FarmBenchmarkStanding } from './colors';
import { formatValue, metricLabel } from './format';

const WIDTH = 160;
const HEIGHT = 36;
/** Keeps the line's stroke and end dots inside the box at the top and bottom of the range. */
const PAD = 2;

/** Weeks listed in the hover card. Enough to read the recent shape without turning it into a table. */
const TOOLTIP_WEEKS = 5;

type Point = [x: number, y: number];

/**
 * Splits a series into the runs of consecutive weeks that have a value, as week indexes. A week without
 * data is a gap in the line, not a zero, and drawing across it would invent a figure for it.
 */
const runs = (values: (number | null)[]): number[][] => {
  const result: number[][] = [];
  let current: number[] = [];

  values.forEach((value, index) => {
    if (value === null) {
      if (current.length) result.push(current);
      current = [];
      return;
    }
    current.push(index);
  });

  if (current.length) result.push(current);
  return result;
};

const pathOf = (points: Point[]) => points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x},${y}`).join(' ');

const TrendTooltip = ({ metric, anchor }: { metric: FarmBenchmarkMetric; anchor: AnchorRect }) => {
  const { t } = useTranslation();
  const { formatDate } = useFarmDateTime();
  const recent = metric.weeks.slice(-TOOLTIP_WEEKS);
  const value = (figure: number | null) => (figure === null ? '-' : formatValue(figure, metric.unit));

  return (
    <HoverCard anchor={anchor} className="min-w-64">
      <p className="mb-1.5 font-medium text-gray-700">{metricLabel(metric.metric, t)}</p>

      <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 gap-y-1">
        <span />
        <span className="text-right text-[11px] uppercase tracking-wide text-gray-400">
          {t('f-benchmark-legend-farm')}
        </span>
        <span className="text-right text-[11px] uppercase tracking-wide text-gray-400">
          {t('f-benchmark-column-average')}
        </span>

        {recent.map((week) => (
          <React.Fragment key={week.weekStartDate}>
            <span className="text-gray-500">
              {t('f-benchmark-week-of', { date: formatDate(benchmarkDay(week.weekStartDate)) })}
            </span>
            <span className="text-right font-medium tabular-nums text-gray-900">{value(week.farmValue)}</span>
            <span className="text-right tabular-nums text-gray-600">{value(week.othersAverage)}</span>
          </React.Fragment>
        ))}
      </div>

      <p className="mt-2 border-t border-gray-100 pt-1.5 text-gray-500">
        {t('f-benchmark-trend-footer', { count: recent.length })}
      </p>
    </HoverCard>
  );
};

export interface BenchmarkTrendProps {
  metric: FarmBenchmarkMetric;
  standing: FarmBenchmarkStanding;
}

/**
 * The 52 weeks behind the twelve-month bar, as a line in the width of a table cell: the farm in the row's
 * colour, the other farms' average dashed grey over their lowest-to-highest band. Not zero-based - unlike
 * the bar, it is read for its shape, and a growth figure moving between 680 and 720 g/day would be flat
 * on an axis from zero.
 *
 * Hand-drawn SVG for the same reason as `FarmTrendSparkline`: one per row, and recharts mounts a resize
 * observer per instance.
 */
export default function BenchmarkTrend({ metric, standing }: BenchmarkTrendProps) {
  const { t } = useTranslation();
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const { anchor, openByPointer, openByFocus, clear } = useAnchorRect(wrapperRef);

  const weeks: FarmBenchmarkWeek[] = metric.weeks;
  const values = weeks.flatMap((week) => [week.farmValue, week.othersMin, week.othersMax, week.othersAverage]);
  const present = values.filter((value): value is number => value !== null);

  if (weeks.length === 0 || present.length === 0) {
    return <div className="text-xs text-gray-400">{t('f-benchmark-no-data')}</div>;
  }

  const low = Math.min(...present);
  const high = Math.max(...present);
  const span = high - low || Math.abs(high) || 1;
  const step = weeks.length > 1 ? (WIDTH - 2 * PAD) / (weeks.length - 1) : 0;

  const x = (index: number) => PAD + index * step;
  const y = (value: number) => PAD + (1 - (value - low) / span) * (HEIGHT - 2 * PAD);

  // The band is drawn only where both edges exist, as one closed shape per run of such weeks: along the
  // top edge, then back along the bottom one.
  const bandPaths = runs(weeks.map((week) => (week.othersMin === null ? null : week.othersMax))).map((run) => {
    const top = run.map((i): Point => [x(i), y(weeks[i].othersMax ?? low)]);
    const bottom = [...run].reverse().map((i): Point => [x(i), y(weeks[i].othersMin ?? low)]);
    return `${pathOf([...top, ...bottom])} Z`;
  });

  const linePoints = (pick: (week: FarmBenchmarkWeek) => number | null) =>
    runs(weeks.map(pick)).map((run) => run.map((i): Point => [x(i), y(pick(weeks[i]) ?? low)]));

  const averageRuns = linePoints((week) => week.othersAverage);
  const farmRuns = linePoints((week) => week.farmValue);

  return (
    <div ref={wrapperRef} onMouseEnter={openByPointer} onMouseLeave={clear} onFocus={openByFocus} onBlur={clear}>
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        tabIndex={0}
        aria-label={t('f-benchmark-trend-label', { metric: metricLabel(metric.metric, t), count: weeks.length })}
        className="block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        {bandPaths.map((d) => (
          <path key={d} d={d} fill={c.range} />
        ))}

        {averageRuns.map((run) => (
          <path
            key={`avg-${run[0][0]}`}
            d={pathOf(run)}
            fill="none"
            stroke={c.average}
            strokeWidth={1}
            strokeDasharray="2 2"
          />
        ))}

        {/* A run of one week has no length to stroke, so it is drawn as a dot. */}
        {farmRuns.map((run) =>
          run.length === 1 ? (
            <circle key={`farm-${run[0][0]}`} cx={run[0][0]} cy={run[0][1]} r={1.75} fill={c[standing]} />
          ) : (
            <path
              key={`farm-${run[0][0]}`}
              d={pathOf(run)}
              fill="none"
              stroke={c[standing]}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )
        )}
      </svg>

      {anchor && <TrendTooltip metric={metric} anchor={anchor} />}
    </div>
  );
}
